import React, { useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import {
	useAuthFlow,
	AUTH_MODES,
} from '../../../services/Contexts/AuthFlowContext';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	VStack,
	HStack,
	useToast,
	Icon,
	Text,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverArrow,
	PopoverBody,
	Link,
} from '@chakra-ui/react';
import {
	authFormInputProps,
	authFormLabelProps,
	authFormLinkProps,
	authFormMutedTextProps,
	authFormPrimaryButtonProps,
	authFormShellProps,
} from './authFormTheme';
import { getSignupAttribution } from '../../../utils/utmAttribution';

const RegisterUserForm = ({ onSave, showTitle = true }) => {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
	const { register } = useAuthenticatedUserInfo();
	const { setMode } = useAuthFlow();
	const location = useLocation();
	const hideSignInLink =
		location.pathname === '/' || location.pathname === '/home';
	const toast = useToast();

	const validatePassword = (pwd) => ({
		hasMinLength: pwd.length >= 6,
		hasNumber: /\d/.test(pwd),
		hasSpecialChar: /[!@#$%^&*()_+\-=[{\]};':"|,.<>?/]/.test(pwd),
		hasCapitalLetter: /[A-Z]/.test(pwd),
	});

	const getPasswordStrength = (pwd) => {
		const checks = validatePassword(pwd);
		const passed = Object.values(checks).filter(Boolean).length;
		return { checks, passed, isValid: passed === 4 };
	};

	const handleUserDetailsSubmit = async (e) => {
		e.preventDefault();

		if (!firstName || !lastName || !email || !password) {
			toast({
				title: 'Validation Error',
				description: 'Please fill in all required fields.',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		if (!companyName.trim()) {
			toast({
				title: 'Validation Error',
				description: 'Company name is required for Company Admin registration.',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		const passwordStrength = getPasswordStrength(password);
		if (!passwordStrength.isValid) {
			const failedChecks = [];
			if (!passwordStrength.checks.hasMinLength)
				failedChecks.push('Minimum 6 characters');
			if (!passwordStrength.checks.hasNumber)
				failedChecks.push('At least one number (0-9)');
			if (!passwordStrength.checks.hasSpecialChar)
				failedChecks.push('At least one special character');
			if (!passwordStrength.checks.hasCapitalLetter)
				failedChecks.push('At least one capital letter (A-Z)');

			toast({
				title: 'Password Requirements Not Met',
				description: failedChecks.join(', '),
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
			return;
		}

		try {
			const signupAttribution = getSignupAttribution(location.search);
			await register({
				firstName,
				lastName,
				email,
				password,
				role: 'Company Admin',
				companyName: companyName.trim(),
				...(signupAttribution ? { signupAttribution } : {}),
			});
			toast({
				title: 'Registration Successful',
				description: 'You have been registered successfully.',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			if (onSave) {
				onSave();
			}
		} catch (error) {
			toast({
				title: 'Registration Error',
				description: error.message || 'Failed to register user.',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const passwordStrength = getPasswordStrength(password);

	return (
		<Box
			{...authFormShellProps}
			as='form'
			onSubmit={handleUserDetailsSubmit}>
			{showTitle && (
				<Text
					fontSize='xl'
					fontWeight='bold'
					color='gray.800'
					mb={4}
					textAlign='center'>
					Register a new account
				</Text>
			)}
			<VStack spacing={4} align='stretch'>
				<FormControl isRequired>
					<FormLabel {...authFormLabelProps}>Company name</FormLabel>
					<Input
						placeholder='Acme Corporation'
						value={companyName}
						onChange={(e) => setCompanyName(e.target.value)}
						{...authFormInputProps}
					/>
				</FormControl>

				<HStack spacing={4} align='flex-start'>
					<FormControl isRequired>
						<FormLabel {...authFormLabelProps}>First name</FormLabel>
						<Input
							placeholder='John'
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							{...authFormInputProps}
						/>
					</FormControl>
					<FormControl isRequired>
						<FormLabel {...authFormLabelProps}>Last name</FormLabel>
						<Input
							placeholder='Smith'
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							{...authFormInputProps}
						/>
					</FormControl>
				</HStack>

				<FormControl isRequired>
					<FormLabel {...authFormLabelProps}>Email</FormLabel>
					<Input
						type='email'
						placeholder='john.smith@acmecorp.com'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						{...authFormInputProps}
					/>
				</FormControl>

				<FormControl isRequired>
					<FormLabel {...authFormLabelProps}>Password</FormLabel>
					<Popover
						isOpen={showPasswordTooltip}
						onClose={() => setShowPasswordTooltip(false)}
						placement='right'
						trigger='manual'>
						<PopoverTrigger>
							<InputGroup>
								<Input
									type={showPassword ? 'text' : 'password'}
									placeholder='Create a strong password'
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									onFocus={() => setShowPasswordTooltip(true)}
									onBlur={() => setShowPasswordTooltip(false)}
									pr='3rem'
									{...authFormInputProps}
								/>
								<InputRightElement h='full'>
									<Button
										size='sm'
										variant='ghost'
										color='gray.500'
										onClick={() => setShowPassword((show) => !show)}
										tabIndex={-1}
										aria-label={showPassword ? 'Hide password' : 'Show password'}>
										<Icon as={showPassword ? FiEyeOff : FiEye} />
									</Button>
								</InputRightElement>
							</InputGroup>
						</PopoverTrigger>
						<PopoverContent
							bg='white'
							borderColor='gray.200'
							boxShadow='lg'
							borderRadius='xl'
							minW='260px'>
							<PopoverArrow bg='white' />
							<PopoverBody p={4}>
								<VStack align='start' spacing={2}>
									<Text fontSize='xs' fontWeight='700' color='gray.700'>
										Password requirements
									</Text>
									{[
										['hasMinLength', 'Minimum 6 characters'],
										['hasNumber', 'At least one number'],
										['hasSpecialChar', 'At least one special character'],
										['hasCapitalLetter', 'At least one capital letter'],
									].map(([key, label]) => (
										<Text
											key={key}
											fontSize='xs'
											color={
												passwordStrength.checks[key] ? 'green.600' : 'gray.500'
											}
											fontWeight={passwordStrength.checks[key] ? '600' : '400'}>
											{passwordStrength.checks[key] ? '✓' : '○'} {label}
										</Text>
									))}
								</VStack>
							</PopoverBody>
						</PopoverContent>
					</Popover>
				</FormControl>

				<Button
					type='submit'
					rightIcon={<Icon as={FaArrowRight} />}
					{...authFormPrimaryButtonProps}
					isDisabled={!password || !passwordStrength.isValid}>
					Create account
				</Button>

				{!hideSignInLink && (
					<Text {...authFormMutedTextProps} mt={2}>
						Already have an account?{' '}
						<Link
							{...authFormLinkProps}
							cursor='pointer'
							onClick={() => setMode(AUTH_MODES.SIGN_IN)}>
							Sign in
						</Link>
					</Text>
				)}
			</VStack>
		</Box>
	);
};

export default RegisterUserForm;
